import { Injectable } from '@angular/core';
import { AuthorizationPolicy } from '@microsoft/microsoft-graph-types-beta';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, distinct, last, map, mergeMap, Observable, of, switchMap } from 'rxjs';
import * as actions from './actions';
import { retry } from 'projects/angular-clarity/src/app/pipes/retry.pipe';
import { TenantAjaxService } from 'projects/angular-clarity/src/app/services/ajax/tenant-ajax.service';
import { skipUntilTenantLoaded } from 'projects/angular-clarity/src/app/services/blob.service';

@Injectable()
export class GraphAuthorizationPolicyEffects {
    loadGraphAuthorizationPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.loadAuthorizationPolicy),
            distinct((action) => action._tenant),
            skipUntilTenantLoaded(this.store),
            mergeMap(({ _tenant }) =>
                this.get(_tenant).pipe(
                    retry(3000, 3, 'graph timeout'),
                    last(),
                    map((data: AuthorizationPolicy) => {
                        return actions.loadAuthorizationPolicySuccess({ _tenant, policy: data });
                    }),
                    catchError((error) => of(actions.loadAuthorizationPolicyFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    updateGraphAuthorizationPolicy$ = createEffect(() =>
        this.actions$.pipe(
            ofType(actions.updateAuthorizationPolicy),
            mergeMap(({ _tenant, policy }) =>
                this.patch(_tenant, policy).pipe(
                    map((policy) => actions.updateAuthorizationPolicySuccess({ _tenant, policy })),
                    catchError((error) => of(actions.updateAuthorizationPolicyFailure({ _tenant, error }))),
                ),
            ),
        ),
    );

    private get(tenant: string) {
        return this.ajax
            .get<{ value: AuthorizationPolicy[] }>(tenant, '/api/microsoft/graph/policies/authorizationPolicy')
            .pipe(map((res) => res.value[0]));
    }

    private patch(tenant: string, policy: Partial<AuthorizationPolicy>): Observable<AuthorizationPolicy> {
        const update$ = this.ajax.patch(
            tenant,
            `/api/microsoft/graph/policies/authorizationPolicy/authorizationPolicy`,
            policy,
        );

        return update$.pipe(switchMap((_) => this.get(tenant)));
    }

    constructor(
        private actions$: Actions,
        private ajax: TenantAjaxService,
        private store: Store,
    ) {}
}
